// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import { useSafeImageSrc } from '../../hooks/useSafeImageSrc';

type ProviderAccessPageShellProps = Readonly<{
  brokerageName?: string;
  brokerageLogoUrl?: string | null;
  children: React.ReactNode;
}>;

/**
 * Shared full-page layout for Provider Access status/error pages.
 * Renders a header with brokerage branding, centered content area, and a footer.
 */
export function ProviderAccessPageShell({
  brokerageName,
  brokerageLogoUrl,
  children,
}: ProviderAccessPageShellProps) {
  const logoSrc = useSafeImageSrc(brokerageLogoUrl);
  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F5]">
      {/* Header */}
      <div className="w-full h-[80px] bg-white border-b border-[#E1E1E1] flex items-center justify-between px-[120px] py-[10px] shrink-0">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={`${brokerageName ?? 'Brokerage'} logo`}
            className="h-[40px] object-contain"
          />
        ) : (
          <div className="text-black font-sans text-body-01 font-medium">
            {brokerageName ?? ''}
          </div>
        )}
        <div className="flex items-center gap-sm">
          <span className="text-black font-sans text-body-03 font-normal">Powered by</span>
          <img src="/finertylogo.svg" alt="Finerty" className="h-[24px]" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center">
        {children}
      </div>

      {/* Footer */}
      <div className="h-[80px] w-full bg-white border-t border-[#E1E1E1] flex items-center justify-between px-[120px] py-md shrink-0">
        <div className="text-black font-sans text-body-04 font-normal leading-normal">
          Copyrights &copy; 2026 Finerty Data Limited. All Rights Reserved.
        </div>
        <div className="flex items-center gap-[10px]">
          <a
            href="https://www.finertydata.com/legal/privacy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="text-black font-sans text-body-04 font-normal leading-normal underline">
              Privacy Policy
            </div>
          </a>
          <div className="w-[1px] h-[15px] bg-black" />
          <a
            href="https://www.finertydata.com/legal/terms/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="text-black font-sans text-body-04 font-normal leading-normal underline">
              Terms &amp; Conditions
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}
